import React, { useMemo } from 'react';
import classnames from 'classnames';
import { formatPriceCents } from 'utils';
import { getCurrencySymbol } from 'utils/currency';
import { IBookingAllocationState, IDepositAccountAllocationState } from '../types';
import { calculateTotalBookingsAllocatedCents, calculateBalanceToAllocateCents } from '../utils';

interface IAllocationSummaryProps {
  totalReceiptCents: number;
  bookingAllocations: IBookingAllocationState[];
  depositAllocation: IDepositAccountAllocationState;
  exchangeCurrency: string;
}

export const AllocationSummary: React.FC<IAllocationSummaryProps> = ({
  totalReceiptCents,
  bookingAllocations,
  depositAllocation,
  exchangeCurrency,
}) => {
  // Calculate total allocated from selected bookings
  const totalBookingsAllocatedCents = useMemo(() => {
    return calculateTotalBookingsAllocatedCents(bookingAllocations);
  }, [bookingAllocations]);

  // Calculate total allocated to deposit
  const totalDepositAllocatedCents = depositAllocation.amountCents;

  // Calculate balance to allocate
  const balanceToAllocateCents = useMemo(() => {
    return calculateBalanceToAllocateCents(
      totalReceiptCents,
      totalBookingsAllocatedCents,
      totalDepositAllocatedCents
    );
  }, [totalReceiptCents, totalBookingsAllocatedCents, totalDepositAllocatedCents]);

  // Determine balance color
  const balanceColorClass = useMemo(() => {
    if (balanceToAllocateCents > 0) return 'text-status-confirmed'; // green
    if (balanceToAllocateCents < 0) return 'text-status-cancelled'; // red
    return 'text-black'; // zero
  }, [balanceToAllocateCents]);

  // Get currency symbol for display
  const currencySymbol = getCurrencySymbol(exchangeCurrency as any);

  return (
    <div className="font-pt-sans mt-30px">
      <div className="bg-teal-20 rounded p-[15px] flex flex-col gap-[5px]">
        {/* Total Receipt */}
        <div className="flex justify-between items-center gap-[20px]">
          <span className="font-pt-sans font-bold text-[13px] uppercase tracking-[0.04em] text-black">
            Total Receipt
          </span>
          <span className="font-pt-sans font-bold text-[19px] text-black">
            {currencySymbol} {formatPriceCents(totalReceiptCents)}
          </span>
        </div>

        {/* Total Bookings Allocated */}
        <div className="flex justify-between items-center gap-[20px]">
          <span className="font-pt-sans text-[13px] uppercase tracking-[0.04em] text-black">
            Total Bookings Allocated
          </span>
          <span className="font-pt-sans text-[16px] text-black">
            {currencySymbol} {formatPriceCents(totalBookingsAllocatedCents)}
          </span>
        </div>

        {/* Total Deposit Allocated */}
        <div className="flex justify-between items-center gap-[20px]">
          <span className="font-pt-sans text-[13px] uppercase tracking-[0.04em] text-black">
            Total Deposit Allocated
          </span>
          <span className="font-pt-sans text-[16px] text-black">
            {currencySymbol} {formatPriceCents(totalDepositAllocatedCents)}
          </span>
        </div>

        {/* Balance to Allocate - with top padding separator */}
        <div className="flex justify-between items-center gap-[20px] pt-[10px]">
          <span className="font-pt-sans text-[13px] uppercase tracking-[0.04em] text-black">
            Balance to Allocate
          </span>
          <span className={classnames('font-pt-sans text-[16px]', balanceColorClass)}>
            {currencySymbol} {formatPriceCents(balanceToAllocateCents)}
          </span>
        </div>
      </div>
    </div>
  );
};
